import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Container,
  Copy,
  Flex,
  Heading,
  Link,
  Section,
  Stack,
  SvgDownload,
} from '@grubhubprod/gh-cookbook-react';
import { format as dateFormat } from 'date-fns';
import { defaultUploadStatus } from 'src/components/CSVUploadTracking/CSVUploadTracking.utils';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { SsoIdentifierTypes } from 'src/types/Corp';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { CSVUploadTracking } from 'src/components/CSVUploadTracking/CSVUploadTracking';
import { EnterpriseEmployeesTable } from 'src/components/EnterpriseSignupSteps/EnterpriseEmployeesStep/EnterpriseEmployeesTable/EnterpriseEmployeesTable';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { getConfig } from 'src/utilities/config';
import { isFunction } from 'src/utilities/is-function';
import { sdk } from 'src/utilities/sdk';
import { pushError, pushErrorFromAPI } from 'src/utilities/notifications';
import { useAsyncDownloadTask } from 'src/utilities/useAsyncDownloadTask/useAsyncDownloadTask';
import { AsyncTaskStatus } from 'src/utilities/useAsyncTask';
import { MediaTypes, saveFile } from 'src/utilities/saveFile';

export function EnterpriseEmployeesStep(): JSX.Element {
  const { corpData } = useAuth();

  const [csvUploadFileUrl, setCsvUploadFileUrl] = useState('');
  const { sendTealiumPageView } = useAnalytics();
  const { goToNextStep, goToPreviousStep, corpId } =
    useEnterpriseSignupStatus();
  const { status, startAsyncTask, downloadUrl } = useAsyncDownloadTask();
  const [currentUploadStatus, setCurrentUploadStatus] =
    useState(defaultUploadStatus);
  const [acceptedCsvFile, setAcceptedCsvFile] = useState<File>(null);

  const configuration = getConfig();
  const [hasEmployeesAdded, setHasEmployeesAdded] = useState(false);
  const setupTableRef = useRef(null);
  const isCorpAdminUiProvider = corpData?.adminUiProvider === 'CORP_ADMIN';
  const isDownloadCompleted = status === AsyncTaskStatus.COMPLETE;
  const isDownloadError = status === AsyncTaskStatus.ERROR;
  const isDownloadInProgress = status === AsyncTaskStatus.IN_PROGRESS;

  useEffect(() => {
    if (
      setupTableRef.current &&
      isFunction(setupTableRef.current.updateSearch) &&
      acceptedCsvFile &&
      (currentUploadStatus.isSuccessful || currentUploadStatus.hasError)
    ) {
      setupTableRef.current.updateSearch();
    }
  }, [currentUploadStatus, acceptedCsvFile]);

  useEffect(() => {
    if (isDownloadCompleted && downloadUrl) {
      // Call to S3
      getCSVFileFromS3(downloadUrl);
    }
    if (isDownloadError) {
      pushError(`We're having trouble downloading your CSV.
                Please try again later or contact your account manager.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloadError, isDownloadCompleted, downloadUrl]);

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'add employees',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (corpData) {
      const { ssoEnabled, ssoIdentifier } = corpData;
      const isSsoIdentifierOther =
        ssoEnabled && ssoIdentifier === SsoIdentifierTypes.OTHER;

      if (isSsoIdentifierOther) {
        setCsvUploadFileUrl('sample-csv-SSO-identifier-cabs.csv');
      } else {
        setCsvUploadFileUrl('employees-upload-v2-sample-cabs.csv');
      }
    }
  }, [corpData]);
  async function downloadEmployeeCsv() {
    const host = isCorpAdminUiProvider ? '/corp-admin' : '/corps';
    const url = isCorpAdminUiProvider
      ? 'download'
      : 'create_diner_download_request';

    try {
      await startAsyncTask(() => {
        return sdk.http.post(`${host}/${corpId}/diners/${url}`, {});
      });
    } catch (err) {
      pushErrorFromAPI(err);
    }
  }

  async function getCSVFileFromS3(downloadUrl: string) {
    try {
      const response = await fetch(downloadUrl);
      const data = await response.text();
      const today = dateFormat(new Date(), 'MMMddyyyy');
      const filename = `${corpData.name}_Employee_List_${today}.csv`;
      saveFile(data, filename, MediaTypes.TEXT_CSV);
    } catch (e) {
      pushErrorFromAPI(e);
    }
  }

  return (
    <Section inset="lg">
      <Container variant="md">
        <Stack y="x-sm">
          <Flex alignItems="center" justifyContent="space-between">
            <Heading variant="h3">Now, add employees</Heading>
            <EnterpriseBackToHomeButton />
          </Flex>
        </Stack>
        <Stack y="md">
          <CSVUploadTracking
            allowMultipleUploads
            header={() => (
              <Stack y="md">
                <Copy variant="body" textAlign="left">
                  <div>
                    Upload a list of your employees with{' '}
                    <Link
                      href={`${configuration.assetsURL}/${csvUploadFileUrl}`}
                      download={csvUploadFileUrl}
                    >
                      our template
                    </Link>{' '}
                    or add individually. Make sure your file fits{' '}
                    <Link
                      href={`${configuration.enterpriseCsvGuidelinesURL}`}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      our guidelines
                    </Link>
                    . You must assign employees to a group before they can place
                    an order. If you want, you can also assign them to a
                    location or department.
                  </div>
                </Copy>
              </Stack>
            )}
            csvUploadFile={csvUploadFileUrl}
            corpId={corpId}
            showTryAgainBtn
            updateCurrentUploadStatusCb={(statusProps) =>
              setCurrentUploadStatus(statusProps)
            }
            acceptedCsvFileCb={(acceptedCsvFile: File) =>
              setAcceptedCsvFile(acceptedCsvFile)
            }
          />
        </Stack>
        <Stack y="sm">
          <Flex alignItems="center">
            <Stack x="sm">
              <Heading variant="h6">Employees added</Heading>
            </Stack>
            {hasEmployeesAdded && (
              <Button
                variant="tertiary"
                type="button"
                buttonSize="medium"
                className="removeButtonPadding"
                onClick={downloadEmployeeCsv}
                isLoading={isDownloadInProgress}
                disabled={isDownloadInProgress}
              >
                <Flex alignItems="center">
                  <Stack x="xxx-sm">Download</Stack>
                  <SvgDownload />
                </Flex>
              </Button>
            )}
          </Flex>
        </Stack>
        <EnterpriseEmployeesTable
          corpId={corpId}
          hasItemsCb={(hasAddedEmployees) =>
            setHasEmployeesAdded(hasAddedEmployees)
          }
          isCsvFileAccepted={!!acceptedCsvFile}
          currentUploadStatus={currentUploadStatus}
        />
        <SignupStepFooter containerSize="md">
          <Flex justifyContent="space-between">
            <Button
              variant="tertiary"
              buttonSize="medium"
              onClick={goToPreviousStep}
            >
              Back
            </Button>
            <Flex alignItems="center">
              <Button
                variant="primary"
                buttonSize="medium"
                disabled={!hasEmployeesAdded}
                onClick={goToNextStep}
              >
                Save and continue
              </Button>
            </Flex>
          </Flex>
        </SignupStepFooter>
      </Container>
    </Section>
  );
}
